import { OT_SHIFT_CODES_KEYS } from '../../shiftCodes';
import type { Shift, RecurringShift } from '../../schema';

export interface ShiftPatternData {
  shiftPattern: string;
  user_id: string;
  dollarPerShift: number;
  noppvPerShift: number;
}

// Shift pattern definitions based on time ranges
const SHIFT_PATTERNS = {
  '6am-2pm': { start: '06:00', end: '14:00' },
  '2pm-10pm': { start: '14:00', end: '22:00' },
  '10pm-6am': { start: '22:00', end: '06:00' },
  '6am-6pm': { start: '06:00', end: '18:00' },
  '6pm-6am': { start: '18:00', end: '06:00' }
};

// Helper function to determine shift pattern from time strings
const getShiftPattern = (startTime: string, endTime: string): string | null => {
  for (const [pattern, times] of Object.entries(SHIFT_PATTERNS)) {
    if (times.start === startTime && times.end === endTime) {
      return pattern;
    }
  }
  return null;
};

// Helper function to calculate shift duration in hours
const getShiftDurationHours = (startTime: string, endTime: string): number => {
  const [startHour, startMin] = startTime.split(':').map(Number);
  const [endHour, endMin] = endTime.split(':').map(Number);
  
  let startMinutes = startHour * 60 + startMin;
  let endMinutes = endHour * 60 + endMin;
  
  // Handle overnight shifts (end time is next day)
  if (endMinutes <= startMinutes) {
    endMinutes += 24 * 60;
  }
  
  return (endMinutes - startMinutes) / 60;
};

export const calculateShiftPatternData = (
  shifts: Shift[],
  recurringShifts: RecurringShift[]
): Record<string, ShiftPatternData[]> => {
  // Skip if no data
  if (!shifts.length || !recurringShifts.length) {
    return {};
  }

  // Filter shifts to only include submitted shifts and exclude OT/COVER shifts (except OT WKND FP)
  const validShifts = shifts.filter(shift => 
    shift.submitted && 
    !(OT_SHIFT_CODES_KEYS.includes(shift.shift_code) && shift.shift_code !== 'OT WKND FP') &&
    shift.shift_code !== 'COVER'
  );

  // Group shifts by user, model, and day type (weekday/weekend) for earnings calculation
  const shiftsByUserModelDay = new Map<string, Shift[]>();
  validShifts.forEach(shift => {
    if (!shift.user_id) return;
    
    // Convert JS date day to recurring shift day format for comparison
    const isWeekend = shift.start_date_time.isWeekend;
    const dayType = isWeekend ? 'weekend' : 'weekday';
    
    const key = `${shift.user_id}-${shift.model_id}-${dayType}`;
    if (!shiftsByUserModelDay.has(key)) {
      shiftsByUserModelDay.set(key, []);
    }
    shiftsByUserModelDay.get(key)!.push(shift);
  });

  // Process recurring shifts to determine patterns
  const resultsByModel: Record<string, ShiftPatternData[]> = {};
  
  // Track user-model-pattern combinations to determine day suffixes
  const userModelPatternDays = new Map<string, Set<number>>();

  recurringShifts.forEach(recurringShift => {
    const { model_id, user_id, start_time, end_time, day_of_week } = recurringShift;
    
    // Skip if no user assigned
    if (!user_id) return;

    // Determine shift pattern from time strings
    const shiftPattern = getShiftPattern(start_time, end_time);
    if (!shiftPattern) return;

    // Track which days this user-model-pattern combination works
    const userModelPatternKey = `${user_id}-${model_id}-${shiftPattern}`;
    if (!userModelPatternDays.has(userModelPatternKey)) {
      userModelPatternDays.set(userModelPatternKey, new Set());
    }
    userModelPatternDays.get(userModelPatternKey)!.add(day_of_week);
  });

  // Track processed combinations to avoid duplicates
  const processedCombinations = new Set<string>();

  recurringShifts.forEach(recurringShift => {
    const { model_id, user_id, start_time, end_time } = recurringShift;
    
    // Skip if no user assigned
    if (!user_id) return;

    // Determine shift pattern from time strings
    const baseShiftPattern = getShiftPattern(start_time, end_time);
    if (!baseShiftPattern) return;

    // Get shift duration for calculations
    const shiftDurationHours = getShiftDurationHours(start_time, end_time);
    if (shiftDurationHours <= 0) return;

    // Check if we've already processed this user-model-pattern combination
    const userModelPatternKey = `${user_id}-${model_id}-${baseShiftPattern}`;
    if (processedCombinations.has(userModelPatternKey)) {
      return; // Skip duplicate
    }
    processedCombinations.add(userModelPatternKey);

    // Determine day suffix based on which days this user works this pattern
    const workingDays = userModelPatternDays.get(userModelPatternKey) || new Set();
    let daySuffix = '';
    
    if (workingDays.has(5) && workingDays.has(6)) {
      // Works both Saturday and Sunday
      daySuffix = ' (Both)';
    } else if (workingDays.has(5)) {
      // Works only Saturday
      daySuffix = ' (Sat)';
    } else if (workingDays.has(6)) {
      // Works only Sunday
      daySuffix = ' (Sun)';
    }
    // If working weekdays only (day_of_week 0), no suffix

    const shiftPattern = baseShiftPattern + daySuffix;

    // Determine day type for earnings calculation
    const isWeekendPattern = workingDays.has(5) || workingDays.has(6);
    const dayType = isWeekendPattern ? 'weekend' : 'weekday';

    // Calculate earnings per shift from actual shift data for this day type
    const userModelDayKey = `${user_id}-${model_id}-${dayType}`;
    const userModelShifts = shiftsByUserModelDay.get(userModelDayKey) || [];
    
    let totalPPV = 0;
    let totalNonPPV = 0;
    let totalShifts = 0;
    let totalHours = 0;

    userModelShifts.forEach(shift => {
      const shiftHours = shift.end_date_time.diff(shift.start_date_time, "hours").hours;
      totalPPV += shift.ppv_earned || 0;
      totalNonPPV += shift.noppv_earned || 0;
      totalShifts++;
      totalHours += shiftHours;
    });

    // Calculate per-shift earnings
    let dollarPerShift = 0;
    let noppvPerShift = 0;

    if (totalHours > 0) {
      // Calculate hourly rates and multiply by expected shift duration
      const ppvPerHour = totalPPV / totalHours;
      const noppvPerHour = totalNonPPV / totalHours;
      
      dollarPerShift = ppvPerHour * shiftDurationHours;
      noppvPerShift = noppvPerHour * shiftDurationHours;
    }

    // Initialize model array if not exists
    if (!resultsByModel[model_id]) {
      resultsByModel[model_id] = [];
    }

    // Add shift pattern data
    resultsByModel[model_id].push({
      shiftPattern,
      user_id,
      dollarPerShift: parseFloat(dollarPerShift.toFixed(2)),
      noppvPerShift: parseFloat(noppvPerShift.toFixed(2))
    });
  });

  // Define custom shift pattern order (base patterns)
  const basePatternOrder = ['6am-2pm', '2pm-10pm', '10pm-6am', '6am-6pm', '6pm-6am'];
  
  // Define suffix order for consistent sorting within same base pattern
  const suffixOrder = ['', ' (Sat)', ' (Sun)', ' (Both)'];

  // Sort results by shift pattern and user_id for consistency
  Object.values(resultsByModel).forEach(modelData => {
    modelData.sort((a, b) => {
      if (a.shiftPattern !== b.shiftPattern) {
        // Extract base pattern and suffix for both patterns
        const getBaseAndSuffix = (pattern: string) => {
          const suffixes = [' (Both)', ' (Sat)', ' (Sun)'];
          for (const suffix of suffixes) {
            if (pattern.endsWith(suffix)) {
              return { base: pattern.slice(0, -suffix.length), suffix };
            }
          }
          return { base: pattern, suffix: '' };
        };
        
        const { base: aBase, suffix: aSuffix } = getBaseAndSuffix(a.shiftPattern);
        const { base: bBase, suffix: bSuffix } = getBaseAndSuffix(b.shiftPattern);
        
        // First sort by base pattern order
        const aBaseIndex = basePatternOrder.indexOf(aBase);
        const bBaseIndex = basePatternOrder.indexOf(bBase);
        
        if (aBaseIndex !== bBaseIndex) {
          // If both base patterns are in the order array, sort by their position
          if (aBaseIndex !== -1 && bBaseIndex !== -1) {
            return aBaseIndex - bBaseIndex;
          }
          
          // If only one is in the order array, prioritize it
          if (aBaseIndex !== -1) return -1;
          if (bBaseIndex !== -1) return 1;
          
          // If neither is in the order array, fall back to alphabetical
          return aBase.localeCompare(bBase);
        }
        
        // If base patterns are the same, sort by suffix order
        const aSuffixIndex = suffixOrder.indexOf(aSuffix);
        const bSuffixIndex = suffixOrder.indexOf(bSuffix);
        
        if (aSuffixIndex !== -1 && bSuffixIndex !== -1) {
          return aSuffixIndex - bSuffixIndex;
        }
        
        // Fall back to alphabetical suffix comparison
        return aSuffix.localeCompare(bSuffix);
      }
      return a.user_id.localeCompare(b.user_id);
    });
  });

  return resultsByModel;
};